import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { getAuth, onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { auth } from '../firebase';
import axios from 'axios';
import { setupAxiosInterceptors, checkPermission, getDeviceFingerprint } from '../assets/Utils';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [rolePermissions, setRolePermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [isProcessingAuth, setIsProcessingAuth] = useState(false);
  const [authRetryCount, setAuthRetryCount] = useState(0);
  const MAX_AUTH_RETRIES = 3;
  
  // MFA state
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaPendingData, setMfaPendingData] = useState(null);
  const [accessDenied, setAccessDenied] = useState(null);
  
  // Use useRef to store the idle timer and prevent multiple concurrent operations
  const idleTimerRef = useRef(null);
  const idleTimeout = 30 * 60 * 1000; // 30 minutes

  // Add token refresh functionality
  const isTokenNearExpiration = (token) => {
    if (!token) return true;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiration = payload.exp * 1000; // Convert to milliseconds
      const now = Date.now();
      const timeUntilExpiration = expiration - now;
      
      // Refresh if token expires in less than 5 minutes
      return timeUntilExpiration < 5 * 60 * 1000;
    } catch (error) {
      console.error('Error parsing token:', error);
      return true; // Assume expired if we can't parse
    }
  };

  const refreshTokenIfNeeded = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser || !token) return;

    if (isTokenNearExpiration(token)) {
      try {
        console.log('Token near expiration, refreshing...');
        const newToken = await currentUser.getIdToken(true); // Force refresh
        setToken(newToken);
        console.log('Token refreshed successfully');
        return newToken;
      } catch (error) {
        console.error('Error refreshing token:', error);
        // If refresh fails, logout user
        await logout();
        throw error;
      }
    }
    return token;
  };

  const cleanupOldSession = () => {
    const sessionCreatedAt = localStorage.getItem('sessionCreatedAt');
    if (sessionCreatedAt) {
      const ageHours = (Date.now() - parseInt(sessionCreatedAt)) / (1000 * 60 * 60);
      // If session is older than 24 hours, clean it up
      if (ageHours > 24) {
        console.log('Cleaning up old session from localStorage');
        localStorage.removeItem('sessionId');
        localStorage.removeItem('sessionCreatedAt');
        return true;
      }
    }
    return false;
  };

  // Always get session ID from localStorage, not from state
  const getCurrentSessionId = () => {
    return localStorage.getItem('sessionId');
  };

  const [sessionId, setSessionId] = useState(() => {
    // Clean up old sessions first
    const oldSessionCleaned = cleanupOldSession();
    // Initialize session ID from localStorage if available and not cleaned
    return oldSessionCleaned ? null : localStorage.getItem('sessionId') || null;
  });

  const generateSessionId = () => {
    return 'session_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
  };

  // Update setSessionIdWithStorage to ensure state is always in sync
  const setSessionIdWithStorage = (id) => {
    const currentSessionId = localStorage.getItem('sessionId');
    if (currentSessionId !== id) {
      if (id) {
        localStorage.setItem('sessionId', id);
        localStorage.setItem('sessionCreatedAt', Date.now().toString());
        setSessionId(id); // Update state after localStorage
      } else {
        localStorage.removeItem('sessionId');
        localStorage.removeItem('sessionCreatedAt');
        setSessionId(null);
      }
    }
  };

  // Use a callback to ensure we get the most current sessionId and token
  const resetIdleTimer = async () => {
    // Refresh token if needed when user is active
    try {
      await refreshTokenIfNeeded();
    } catch (error) {
      console.error('Token refresh failed during activity:', error);
      return; // Don't reset timer if token refresh failed
    }

    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    idleTimerRef.current = setTimeout(() => {
      const handleIdleLogout = async () => {
        const currentSessionId = localStorage.getItem('sessionId');
        const currentToken = auth.currentUser ? await auth.currentUser.getIdToken() : null;
        const fingerprint = localStorage.getItem('deviceFingerprint');
        
        // Call logout endpoint if we have session info
        if (currentSessionId && currentToken) {
          try {
            const logoutHeaders = { 
              Authorization: `Bearer ${currentToken}`,
              'x-session-id': currentSessionId  // Make lowercase for consistency
            };
            // Include device fingerprint so server can logout all sessions on this device
            if (fingerprint) {
              logoutHeaders['x-device-fingerprint'] = fingerprint;
            }
            await axios.post(
              `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/user-activity/logout`,
              { session_id: currentSessionId },
              { headers: logoutHeaders }
            );
            console.log('Session marked as inactive due to idle timeout');
          } catch (error) {
            console.error('Error during idle logout session cleanup:', error);
          }
        }
        
        // Now perform the actual logout
        await signOut(auth);
        setUser(null);
        setToken(null);
        setRolePermissions({});
        setSessionIdWithStorage(null);
        if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
        
        alert('You have been logged out due to inactivity.');
      };
      
      handleIdleLogout();
    }, idleTimeout);
  };

  const validateExistingSession = async (idToken, currentSessionId, trigger = 'restore') => {
    if (!idToken || !currentSessionId) {
      throw new Error('Missing session validation context');
    }

    const storedFingerprint = localStorage.getItem('deviceFingerprint');
    const deviceFingerprint = storedFingerprint || await getDeviceFingerprint();
    if (!storedFingerprint) {
      localStorage.setItem('deviceFingerprint', deviceFingerprint);
    }

    await axios.get(
      `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/user-activity/active-users?trigger=${trigger}`,
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
          'x-session-id': currentSessionId,
          'x-device-fingerprint': deviceFingerprint,
        }
      }
    );

    return deviceFingerprint;
  };

  const isRecentSessionForRestoreRepair = () => {
    const sessionCreatedAt = Number(localStorage.getItem('sessionCreatedAt') || 0);
    if (!sessionCreatedAt) {
      return false;
    }
    return (Date.now() - sessionCreatedAt) <= (2 * 60 * 1000);
  };

  const bootstrapSessionForRestore = async (idToken) => {
    const storedFingerprint = localStorage.getItem('deviceFingerprint');
    const deviceFingerprint = storedFingerprint || await getDeviceFingerprint();
    if (!storedFingerprint) {
      localStorage.setItem('deviceFingerprint', deviceFingerprint);
    }

    const candidateSessionId = generateSessionId();
    setSessionIdWithStorage(candidateSessionId);

    const response = await axios.get(
      `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/user-activity/active-users?trigger=session`,
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
          'x-session-id': candidateSessionId,
          'x-device-fingerprint': deviceFingerprint,
        }
      }
    );

    const serverSessionId = response?.headers?.['x-new-session-id'] || candidateSessionId;
    setSessionIdWithStorage(serverSessionId);
    return serverSessionId;
  };

  useEffect(() => {
    // DON'T call setupAxiosInterceptors here - it causes loops
    // We'll handle auth manually without interceptors during login
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      // Prevent concurrent processing during login/logout operations
      if (isProcessingAuth) {
        console.log('Auth processing already in progress, skipping onAuthStateChanged');
        return;
      }

      console.log('Firebase auth state changed:', firebaseUser ? 'authenticated' : 'signed-out');
      
      if (!firebaseUser) {
        // User logged out - clear everything
        if (user) {
          console.log('User logged out, clearing state');
          setUser(null);
          setToken(null);
          setRolePermissions({});
          setSessionIdWithStorage(null);
          setAuthRetryCount(0); // Reset retry counter
        }
        setLoading(false);
        return;
      }

      // Handle browser refresh case: Firebase user exists but app state is empty
      if (firebaseUser && !user) {
        // If MFA is pending, don't restore session — user must complete MFA first
        if (localStorage.getItem('mfaPending') === 'true') {
          console.log('MFA verification pending, skipping session restoration');
          setLoading(false);
          return;
        }

        // Check retry limit
        if (authRetryCount >= MAX_AUTH_RETRIES) {
          console.error(`Max auth retry attempts (${MAX_AUTH_RETRIES}) reached. Signing out.`);
          await signOut(auth);
          setUser(null);
          setToken(null);
          setRolePermissions({});
          setSessionIdWithStorage(null);
          setAuthRetryCount(0);
          setLoading(false);
          return;
        }

        console.log(`Browser refresh detected - attempt ${authRetryCount + 1}/${MAX_AUTH_RETRIES}`);
        
        try {
          // Get fresh token
          const idToken = await firebaseUser.getIdToken(false);

          // Set up interceptors if not already done
          setupAxiosInterceptors();

          // Get or use existing session ID
          let currentSessionId = getCurrentSessionId();
          if (!currentSessionId) {
            currentSessionId = generateSessionId();
            setSessionIdWithStorage(currentSessionId);
            console.log('Generated session ID for refresh');
          } else {
            console.log('Using existing session ID from localStorage');
          }

          // Fetch user data
          console.log('Restoring user data after refresh');
          const userResponse = await axios.get(
            `${process.env.REACT_APP_SERVER_BASE_URL}${process.env.REACT_APP_API_USERS}/firebase/${firebaseUser.uid}`,
            { 
              headers: { 
                Authorization: `Bearer ${idToken}`,
                'x-session-id': currentSessionId
              } 
            }
          );

          // Fetch role permissions
          console.log('Restoring role permissions after refresh');
          const roleResponse = await axios.get(
            `${process.env.REACT_APP_SERVER_BASE_URL}${process.env.REACT_APP_API_ROLES}/${userResponse.data.role}`,
            { 
              headers: { 
                Authorization: `Bearer ${idToken}`,
                'x-session-id': currentSessionId
              } 
            }
          );

          try {
            await validateExistingSession(idToken, currentSessionId, 'restore');
          } catch (restoreValidationError) {
            const isSessionExpired = restoreValidationError?.response?.status === 401
              && restoreValidationError?.response?.data?.error === 'SESSION_EXPIRED';

            if (isSessionExpired && isRecentSessionForRestoreRepair()) {
              console.warn('Restore session check failed for a fresh session; attempting one-time repair');
              const repairedSessionId = await bootstrapSessionForRestore(idToken);
              await validateExistingSession(idToken, repairedSessionId, 'restore');
            } else {
              throw restoreValidationError;
            }
          }

          // If auth state changed while restore was in flight, abort committing stale state.
          if (!auth.currentUser || auth.currentUser.uid !== firebaseUser.uid) {
            console.warn('Restore aborted: auth state changed while restoring session');
            setLoading(false);
            return;
          }

          // Set all state
          setUser(userResponse.data);
          setToken(idToken);
          setRolePermissions(roleResponse.data.permissions);
          setAuthRetryCount(0); // Reset on success
          resetIdleTimer();
          
          console.log('Session restored successfully after refresh for:', userResponse.data.name);
        } catch (error) {
          console.error('Error restoring session after refresh:', error);
          // Increment retry counter
          setAuthRetryCount(prev => prev + 1);
          console.log(`Auth retry count incremented to: ${authRetryCount + 1}`);
          
          // If max retries reached, sign out of Firebase
          if (authRetryCount + 1 >= MAX_AUTH_RETRIES) {
            console.error('Max retries reached, signing out of Firebase');
            await signOut(auth);
          }

          if (auth.currentUser) {
            await signOut(auth);
          }
          
          // If restore fails, clear everything and let user login again
          setUser(null);
          setToken(null);
          setRolePermissions({});
          setSessionIdWithStorage(null);
        }
        
        setLoading(false);
        return;
      }

      // Don't process if we already have this user set up
      if (user && user.firebase_uid === firebaseUser.uid && token) {
        console.log('User already processed and has token, skipping');
        setLoading(false);
        return;
      }

      // For other cases (like login), let the login function handle the setup
      console.log('Firebase user detected, but letting login function handle the setup');
      setLoading(false);
    });

    return () => {
      unsubscribe();
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [user, token, isProcessingAuth, authRetryCount]); // Add authRetryCount to dependencies

  // Separate useEffect for managing idle timer and user activity listeners
  useEffect(() => {
    // Only set up idle timer if user is logged in
    if (user && token) {
      resetIdleTimer();

      // Add event listeners to reset idle timer on user activity.
      const events = ['mousemove', 'keydown', 'click', 'scroll'];
      events.forEach((event) => {
        window.addEventListener(event, resetIdleTimer);
      });

      // Do not terminate sessions on beforeunload.
      // Full-page navigations/reloads trigger beforeunload and should not be treated as logout.
      // Server-side idle cleanup + explicit logout endpoint are the source of truth.

      // Handle visibility change (browser tab switching, mobile app backgrounding)
      const handleVisibilityChange = async () => {
        if (document.visibilityState === 'visible') {
          // App returned to foreground — validate session server-side.
          // Avoid client-side elapsed-time logout here because browser lifecycle
          // events can be unreliable across devices and cause false sign-outs.
          const currentSessionId = localStorage.getItem('sessionId');
          if (currentSessionId && auth.currentUser) {
            try {
              const freshToken = await auth.currentUser.getIdToken(false);
              setToken(freshToken);
              await axios.get(
                `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/user-activity/active-users?trigger=heartbeat`,
                {
                  headers: {
                    Authorization: `Bearer ${freshToken}`,
                    'x-session-id': currentSessionId,
                    'x-device-fingerprint': localStorage.getItem('deviceFingerprint'),
                  }
                }
              );
              console.log('Heartbeat sent after app became visible');
            } catch (err) {
              console.warn('Heartbeat failed:', err.message);
              if (err.response?.status === 401 || err.response?.status === 403) {
                console.log('Session expired during background, forcing logout');
                logout();
                return;
              }
            }
          }
          resetIdleTimer();
        } else if (document.visibilityState === 'hidden') {
          // Keep the hidden marker for diagnostics only.
          localStorage.setItem('lastHiddenTime', Date.now().toString());
          const currentSessionId = localStorage.getItem('sessionId');
          if (currentSessionId) {
            console.log('Tab hidden, saved timestamp for staleness check');
          }
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        events.forEach((event) => {
          window.removeEventListener(event, resetIdleTimer);
        });
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      };
    }
  }, [user, token, sessionId]); // Include dependencies so the timer uses current values

  useEffect(() => {
    // Listen for session ID updates from the interceptor
    const handleSessionUpdate = (event) => {
      const newSessionId = event.detail.sessionId;
      console.log('useAuth: Received session update event');
      setSessionId(newSessionId);
      if (localStorage.getItem('sessionId') !== newSessionId) {
        localStorage.setItem('sessionId', newSessionId);
        localStorage.setItem('sessionCreatedAt', Date.now().toString());
      }
    };

    // Listen for server-side session revocation (force-logout by admin or DB change)
    const handleSessionRevoked = () => {
      console.warn('useAuth: Session invalidated by server - clearing state and redirecting to login');
      setUser(null);
      setToken(null);
      setRolePermissions({});
      setSessionIdWithStorage(null);
      setMfaRequired(false);
      setMfaPendingData(null);
      setAccessDenied(null);
      localStorage.removeItem('mfaPending');
      // Firebase signOut already called in Utils.js interceptor
    };

    window.addEventListener('sessionIdUpdated', handleSessionUpdate);
    window.addEventListener('sessionRevoked', handleSessionRevoked);

    return () => {
      window.removeEventListener('sessionIdUpdated', handleSessionUpdate);
      window.removeEventListener('sessionRevoked', handleSessionRevoked);
    };
  }, []);

  const login = async (email, password) => {
    if (isProcessingAuth) {
      console.log('Auth processing already in progress, ignoring login request');
      return;
    }
    
    console.log('Starting login process...');
    setIsProcessingAuth(true);
    setLoading(true);
    setAccessDenied(null);
    
    try {
      // Clear any existing session and state
      localStorage.removeItem('sessionId');
      localStorage.removeItem('sessionCreatedAt');
      localStorage.removeItem('mfaPending');
      setSessionId(null);
      setUser(null);
      setToken(null);
      setRolePermissions({});
      
      // Step 1: Firebase authentication
      console.log('Step 1: Firebase authentication');
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      
      // Step 2: Get token ONCE (but do NOT store in state yet — MFA must pass first)
      console.log('Step 2: Getting Firebase token');
      const idToken = await firebaseUser.getIdToken(false);

      // Step 2.5: Check if MFA is required
      console.log('Step 2.5: Checking MFA requirements');
      const deviceFingerprint = await getDeviceFingerprint();
      // Persist fingerprint to localStorage so sendBeacon can access it synchronously on beforeunload
      localStorage.setItem('deviceFingerprint', deviceFingerprint);
      const ipAddress = null; // Server will detect IP
      
      // Fetch user ID first to check MFA
      const userResponse = await axios.get(
        `${process.env.REACT_APP_SERVER_BASE_URL}${process.env.REACT_APP_API_USERS}/firebase/${firebaseUser.uid}`,
        { 
          headers: { 
            Authorization: `Bearer ${idToken}`
          } 
        }
      );

      console.log('Step 2.75: Checking access restrictions');
      const accessCheckResponse = await axios.post(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/access/verify`,
        {
          userId: userResponse.data.portal_id,
        },
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            'x-device-fingerprint': deviceFingerprint,
          },
        }
      );

      if (accessCheckResponse.data && accessCheckResponse.data.allowed === false) {
        const accessDeniedPayload = {
          reason: accessCheckResponse.data.reason,
          message: accessCheckResponse.data.message,
        };
        setMfaRequired(false);
        setMfaPendingData(null);
        localStorage.removeItem('mfaPending');

        // Keep auth processing locked until sign-out completes so the
        // auth-state observer cannot restore a transient session.
        if (auth.currentUser) {
          await signOut(auth);
        }

        setAccessDenied(accessDeniedPayload);
        setIsProcessingAuth(false);
        setLoading(false);
        return { accessDenied: true, ...accessDeniedPayload };
      }

      const mfaCheckResponse = await axios.post(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/mfa/check-required`,
        {
          userId: userResponse.data.portal_id,
          deviceFingerprint,
          ipAddress,
        },
        {
          headers: {
            Authorization: `Bearer ${idToken}`
          }
        }
      );

      if (mfaCheckResponse.data.mfaRequired) {
        console.log('MFA required:', mfaCheckResponse.data.reason);
        // Set up interceptors so MfaCodeEntry's bare axios calls get auth headers
        setupAxiosInterceptors();
        // Store pending auth data and show MFA UI
        setMfaPendingData({
          firebaseUser,
          idToken,
          userResponse: userResponse.data,
          deviceFingerprint,
          reason: mfaCheckResponse.data.message,
          triggerReason: mfaCheckResponse.data.reason,
        });
        setMfaRequired(true);
        localStorage.setItem('mfaPending', 'true');
        setIsProcessingAuth(false);
        setLoading(false);
        return { mfaRequired: true }; // Stop here and wait for MFA verification
      }

      // If MFA not required, continue with normal login
      console.log ('MFA not required, completing login');
      await completLogin(idToken, userResponse.data, deviceFingerprint);
      
      console.log('Login completed successfully for:', userResponse.data.name);
      return { mfaRequired: false };
    } catch (error) {
      console.error('Login error:', error);
      // Clear state on error
      setUser(null);
      setToken(null);
      setRolePermissions({});
      setSessionIdWithStorage(null);
      setMfaRequired(false);
      setMfaPendingData(null);
      setAccessDenied(null);
      throw error;
    } finally {
      if (!mfaRequired) {
        setIsProcessingAuth(false);
        setLoading(false);
      }
    }
  };

  // Complete login after MFA verification (or if MFA not required)
  const completLogin = async (idToken, userData, deviceFingerprint) => {
    try {
      // Step 3: Generate session ID
      console.log('Step 3: Generating session ID');
      const newSessionId = generateSessionId();
      setSessionIdWithStorage(newSessionId);

      // Step 4: Setup axios interceptors AFTER we have token
      console.log('Step 4: Setting up axios interceptors');
      setupAxiosInterceptors();

      // Step 5: Fetch role permissions.
      // NOTE: /roles/ is explicitly excluded from session tracking in trackUserActivity,
      // so this request will NOT create a session - it's safe to fire here.
      console.log('Step 5: Fetching role permissions');
      const roleResponse = await axios.get(
        `${process.env.REACT_APP_SERVER_BASE_URL}${process.env.REACT_APP_API_ROLES}/${userData.role}`,
        { 
          headers: { 
            Authorization: `Bearer ${idToken}`,
            'x-session-id': newSessionId,
            'x-device-fingerprint': deviceFingerprint,
          } 
        }
      );

      // Step 6: Single deliberate call to create the session in the middleware.
      // This is the ONLY request that should trigger session creation during login.
      console.log('Step 6: Triggering session creation');
      try {
        const currentSessionId = getCurrentSessionId(); // Always get from localStorage
        const sessionBootstrapResponse = await axios.get(
          `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/user-activity/active-users?trigger=session`,
          { 
            headers: { 
              Authorization: `Bearer ${idToken}`,
              'x-session-id': currentSessionId,
              'x-device-fingerprint': deviceFingerprint,
            } 
          }
        );

        const serverSessionId = sessionBootstrapResponse?.headers?.['x-new-session-id'];
        if (serverSessionId) {
          setSessionIdWithStorage(serverSessionId);
        } else {
          console.warn('Session bootstrap response missing x-new-session-id; keeping current session ID');
        }
        console.log('Session creation triggered');
      } catch (sessionError) {
        console.error('Error triggering session creation:', sessionError);
        throw sessionError;
      }

      // Step 7: Set logged-in state only after session has been established.
      console.log('Step 7: Setting user state');
      setUser(userData);
      setToken(idToken);
      setRolePermissions(roleResponse.data.permissions);
      setMfaRequired(false);
      setMfaPendingData(null);
      localStorage.removeItem('mfaPending');
      localStorage.removeItem('lastHiddenTime');
      resetIdleTimer();
    } catch (error) {
      console.error('Complete login error:', error);
      throw error;
    }
  };

  // Handle MFA verification success
  const handleMfaSuccess = async () => {
    if (!mfaPendingData) {
      console.error('No pending MFA data');
      return;
    }

    console.log('MFA verification successful, completing login...');
    setIsProcessingAuth(true);
    setLoading(true);

    try {
      await completLogin(
        mfaPendingData.idToken,
        mfaPendingData.userResponse,
        mfaPendingData.deviceFingerprint
      );
      console.log('Login completed successfully after MFA');
    } catch (error) {
      console.error('Error completing login after MFA:', error);
      // Clear state on error
      setUser(null);
      setToken(null);
      setRolePermissions({});
      setSessionIdWithStorage(null);
      setMfaRequired(false);
      setMfaPendingData(null);
      setAccessDenied(null);
      throw error;
    } finally {
      setIsProcessingAuth(false);
      setLoading(false);
    }
  };

  // Handle MFA cancellation
  const handleMfaCancel = async () => {
    console.log('MFA cancelled, logging out...');
    setMfaRequired(false);
    setMfaPendingData(null);
    localStorage.removeItem('mfaPending');
    setUser(null);
    setToken(null);
    setRolePermissions({});
    setSessionIdWithStorage(null);
    setAccessDenied(null);
    
    // Sign out from Firebase
    if (auth.currentUser) {
      await signOut(auth);
    }
  };

  const clearAccessDenied = async () => {
    setAccessDenied(null);
    setMfaRequired(false);
    setMfaPendingData(null);
    localStorage.removeItem('mfaPending');

    if (auth.currentUser) {
      await signOut(auth);
    }
  };

  const logout = async () => {
    if (isProcessingAuth) {
      console.log('Auth processing in progress, ignoring logout');
      return;
    }
    
    if (!user || !token) {
      console.log('No user or token to logout');
      return;
    }
    
    console.log('Starting logout process...');
    setIsProcessingAuth(true);
    
    const currentSessionId = getCurrentSessionId(); // Always get from localStorage
    const currentToken = token;
    // Clear state immediately
    setUser(null);
    setToken(null);
    setRolePermissions({});
    setSessionIdWithStorage(null);
    localStorage.removeItem('deviceFingerprint');
    localStorage.removeItem('mfaPending');
    localStorage.removeItem('lastHiddenTime');
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);

    // Call logout endpoint if we have session info
    if (currentSessionId && currentToken) {
      try {
        console.log('Calling logout endpoint');
        await axios.post(
          `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/user-activity/logout`,
          { session_id: currentSessionId },
          { 
            headers: { 
              Authorization: `Bearer ${currentToken}`,
              'x-session-id': currentSessionId
            } 
          }
        );
        console.log('Session logout completed');
      } catch (error) {
        console.error('Error during logout session cleanup:', error);
      }
    }

    // Firebase signout
    try {
      await signOut(auth);
      console.log('Firebase signout completed');
    } catch (error) {
      console.error('Error during Firebase signout:', error);
    } finally {
      setIsProcessingAuth(false);
      setLoading(false);
    }
  };

  // Expose a helper for permission checking and current session ID
  const hasPermission = (key, type) => {
    return checkPermission(rolePermissions, key, type);
  };

  // Always return the current session ID from localStorage
  const contextValue = {
    user,
    token,
    rolePermissions,
    login,
    logout,
    loading,
    hasPermission,
    sessionId: getCurrentSessionId(), // Always get from localStorage
    refreshTokenIfNeeded, // Expose for manual refresh if needed
    mfaRequired,
    mfaPendingData,
    handleMfaSuccess,
    handleMfaCancel,
    accessDenied,
    clearAccessDenied,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
